// A desk's live browser screen, over VNC (noVNC) through the Integrations microservice.
//
// Opens view-only: keyboard and mouse are not sent. "Take over" asks first, then reconnects
// with a control ticket. Tickets are single-use, so every connect and reconnect mints a new
// one; the ingress can cut a long session, which shows as Disconnected with a Reconnect button.
// noVNC is loaded on first open so the console page doesn't carry it.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Typography, useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import { liveSocketUrl, mintLiveTicket } from './api';

const STATUS = {
    idle: { label: 'Not connected', bg: '#f0f0f0', fg: '#555' },
    connecting: { label: 'Connecting…', bg: '#e3f2fd', fg: '#1565c0' },
    connected: { label: 'Live', bg: '#e6f4ea', fg: '#1e5631' },
    dropped: { label: 'Disconnected', bg: '#fff4e5', fg: '#9a4f0e' },
    error: { label: 'Could not connect', bg: '#fdecea', fg: '#c62828' },
};
// Dark mode: the same hue as a translucent tint on the dialog, with a pale shade as text.
const STATUS_DARK = {
    idle: { bg: 'rgba(255,255,255,0.12)', fg: '#e2e8f0' },
    connecting: { bg: alpha('#5b9cf6', 0.2), fg: '#a9c6fa' },
    connected: { bg: alpha('#66bb6a', 0.2), fg: '#a5d6a7' },
    dropped: { bg: alpha('#ed6c02', 0.2), fg: '#ffcc80' },
    error: { bg: alpha('#c62828', 0.25), fg: '#ef9a9a' },
};

// target: { desk, title, pod, mode: 'view' | 'control', confirmFirst, note } or null.
// running: the login the desk's browser has open right now, if any (for the take-over warning).
const LiveDeskDialog = ({ target, running, onClose }) => {
    const fullScreen = useMediaQuery('(max-width:600px)');
    const dark = useTheme().palette.mode === 'dark';
    const screenRef = useRef(null);
    const rfbRef = useRef(null);
    const attemptRef = useRef(0);
    const [mode, setMode] = useState('view');
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState(null);
    const [pod, setPod] = useState(null);
    const [confirm, setConfirm] = useState(false);

    // Drop the current connection; bumping the attempt makes its disconnect event a no-op.
    const teardown = useCallback(() => {
        attemptRef.current += 1;
        const rfb = rfbRef.current;
        rfbRef.current = null;
        if (rfb) {
            try { rfb.disconnect(); } catch { /* already closed */ }
        }
    }, []);

    const connect = useCallback(async (wanted) => {
        if (!target) return;
        teardown();
        const attempt = attemptRef.current;
        setMode(wanted); setStatus('connecting'); setError(null);
        try {
            const [{ default: RFB }, t] = await Promise.all([import('@novnc/novnc'), mintLiveTicket(target.desk, wanted)]);
            if (attempt !== attemptRef.current) return;
            if (!screenRef.current) throw new Error('The screen area is not ready; press Reconnect.');
            const granted = t.mode || wanted;
            const rfb = new RFB(screenRef.current, liveSocketUrl(t.ws_path, t.ticket), { wsProtocols: ['binary'] });
            rfb.viewOnly = granted !== 'control';
            rfb.scaleViewport = true;
            rfb.resizeSession = false;
            rfb.addEventListener('connect', () => {
                if (attempt === attemptRef.current) setStatus('connected');
            });
            rfb.addEventListener('disconnect', () => {
                if (attempt !== attemptRef.current) return;
                rfbRef.current = null;
                setStatus((s) => (s === 'error' ? s : 'dropped'));
            });
            rfb.addEventListener('securityfailure', (e) => {
                if (attempt !== attemptRef.current) return;
                setStatus('error');
                setError(`The desk refused the connection${e.detail?.reason ? `: ${e.detail.reason}` : ''}.`);
            });
            rfb.addEventListener('credentialsrequired', () => {
                if (attempt !== attemptRef.current) return;
                setStatus('error');
                setError('The desk asked for a VNC password, which the portal does not have. Tell the integrations team.');
                rfb.disconnect();
            });
            rfbRef.current = rfb;
            setMode(granted);
            if (t.pod) setPod(t.pod);
        } catch (e) {
            if (attempt !== attemptRef.current) return;
            setStatus('error');
            setError(e.message);
        }
    }, [target, teardown]);

    // Open: connect, or for a take-over link ask first. Close: disconnect.
    useEffect(() => {
        if (!target) return undefined;
        setPod(target.pod || null);
        setStatus('idle'); setError(null);
        if (target.mode === 'control' && target.confirmFirst) {
            setMode('view');
            setConfirm(true);
        } else {
            connect(target.mode === 'control' ? 'control' : 'view');
        }
        return () => { setConfirm(false); teardown(); };
    }, [target, connect, teardown]);

    const takeOver = () => { setConfirm(false); connect('control'); };
    // Saying no to a take-over link still shows the screen, view only.
    const declineTakeOver = () => { setConfirm(false); if (status === 'idle') connect('view'); };

    const control = mode === 'control';
    const st = dark ? { ...STATUS[status], ...STATUS_DARK[status] } : STATUS[status];
    const close = (e, reason) => { if (reason !== 'backdropClick') onClose(); };

    return (
        <>
            <Dialog open={Boolean(target)} onClose={close} maxWidth="xl" fullWidth fullScreen={fullScreen}
                disableEscapeKeyDown={control}
                PaperProps={{ sx: fullScreen ? {} : { height: '90vh' } }}>
                <DialogTitle className="d-font" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', pb: 1 }}>
                    <Box component="span" data-live-title sx={{ fontSize: 16, fontWeight: 600 }}>
                        {target?.title} · {pod} · {control ? 'you have control' : 'view only'}
                    </Box>
                    <Chip size="small" label={st.label} data-live-status={status}
                        sx={{ fontSize: 12, height: 22, bgcolor: st.bg, color: st.fg }} />
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, pb: 1 }}>
                    {target?.note && <Alert severity="info" sx={{ py: 0 }}>{target.note}</Alert>}
                    {error && <Alert severity="error" sx={{ py: 0 }}>{error}</Alert>}
                    {status === 'dropped' && (
                        <Alert severity="warning" sx={{ py: 0 }}>
                            The connection to the desk closed (long sessions get cut). Reconnect opens it again.
                        </Alert>
                    )}
                    <Box ref={screenRef} data-live-screen data-mode={mode}
                        sx={{ flex: 1, minHeight: 240, bgcolor: '#1e1e1e', borderRadius: 1, overflow: 'hidden', position: 'relative' }} />
                    <Typography className="d-font" sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {control
                            ? 'Your keyboard and mouse go to the desk\'s browser. Close when you\'re done; the desk carries on by itself.'
                            : 'View only: nothing you type or click reaches the desk.'}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    {(status === 'dropped' || status === 'error') && (
                        <Button size="small" startIcon={<RefreshIcon />} sx={{ textTransform: 'none' }} onClick={() => connect(mode)}>
                            Reconnect
                        </Button>
                    )}
                    {!control && (
                        <Button size="small" startIcon={<KeyboardIcon />} sx={{ textTransform: 'none' }} onClick={() => setConfirm(true)}>
                            Take over
                        </Button>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={confirm} onClose={declineTakeOver} maxWidth="xs" fullWidth>
                <DialogTitle className="d-font">Take over {target?.title}?</DialogTitle>
                <DialogContent>
                    <Typography className="d-font" sx={{ fontSize: 13 }}>
                        You&apos;ll be typing into a browser signed in to {running || 'this desk'}. Use it to sign in or
                        clear a check; don&apos;t interrupt a run in progress.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={declineTakeOver}>Cancel</Button>
                    <Button variant="contained" onClick={takeOver}>Take over</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default LiveDeskDialog;
